/**
 * VariantImage — <img> cho ảnh trong kho: nhận CON TRỎ kho ("note-images/…/<uuid>")
 * hoặc URL đầy đủ, ghép biến thể lúc vẽ, và THỬ LẠI CÓ ĐỘ TRỄ khi ảnh hỏng.
 *
 * Vì sao cần: ngay sau khi tải lên, bản thumb/md/lg chưa sinh (worker mất
 * ~1–10 giây) nên kho trả 404. Caddy trước kho gắn `Cache-Control: immutable`
 * lên cả 404, nên Cloudflare lẫn Chrome ghim 404 đó tới một năm — object sinh
 * xong sau 2 giây cũng vô ích, URL đã "chết" trong cache (đo 10/09/2026:
 * `plans/reports/debug-260910-1605-anh-ghi-chu-tao-don-404-cache-immutable.md`).
 *
 * Ba việc component này làm:
 *  - Ảnh VỪA tải lên trong phiên thì vẽ thẳng bằng ẢNH GỐC (`freshOriginalUrl`)
 *    thay vì xin biến thể chưa sinh — không đẻ ra 404 thì không có gì để ghim.
 *  - Đợi hết mốc trễ rồi mới thử lại: thứ còn thiếu là THỜI GIAN, đổi src
 *    tức thì chỉ đâm lại đúng tệp chưa có.
 *  - Gắn khoá phá cache MỚI mỗi lần dựng: không đổi khoá thì chỉ nhận lại bản
 *    404 đã đóng băng ở biên CDN và đĩa trình duyệt.
 *
 * URL đầy đủ (http…) đi qua nguyên vẹn — imageUrl không ghép biến thể — nên
 * caller có sẵn URL ảnh gốc (từ phản hồi upload) cứ đưa thẳng vào `src`.
 */

import { useEffect, useState, type ImgHTMLAttributes } from "react";
import { imageUrl, type Variant } from "@/lib/upload";
import { freshOriginalUrl } from "@/lib/fresh-upload";

// Mốc thử lại (ms). Worker sinh biến thể mất ~1 giây; hai nấc phủ cả lúc hàng
// đợi đang bận. Hết nấc thì thôi — thử mãi chỉ tạo vòng tải không dứt.
const RETRY_DELAYS_MS = [1500, 4000];

/**
 * Khoá phá cache, sinh MỚI mỗi lần component dựng.
 *
 * 🔴 Khoá CỐ ĐỊNH (`?retry=1`, `?retry=2`) KHÔNG cứu được ảnh — đo thật
 * 25/09/2026 trên `cdn.gaonau.com`: query string nằm TRONG khoá cache của
 * Cloudflare, nên `?retry=1` lần đầu trả `cf-cache-status: MISS` rồi lần sau
 * là `HIT` với chính bản 404 `immutable` đó. Hệ quả: một phiên đâm sớm là
 * GIẾT LUÔN cả ba URL (bản gốc + hai nấc thử lại) suốt một năm, mọi người
 * mọi máy về sau đều thấy ảnh vỡ. Khoá mới mỗi lần dựng thì lần thử lại luôn
 * chạm tới kho thật, nên ảnh đã sinh xong từ lâu vẫn hiện lại được.
 */
function freshCacheKey(): string {
  return Math.random().toString(36).slice(2, 8);
}

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "onError"> & {
  /** Con trỏ kho (base_path) hoặc URL đầy đủ. */
  src: string;
  /** Biến thể ghép khi `src` là con trỏ kho. Mặc định md. */
  variant?: Variant;
};

export function VariantImage({ src, variant = "md", alt = "", ...rest }: Props) {
  const [attempt, setAttempt] = useState(0);
  // Đổi ảnh phải đếm lại từ đầu — chỉnh ngay trong lúc render, không qua
  // effect, kẻo tấm mới thừa hưởng số lần thử của tấm cũ rồi bỏ luôn nấc thử
  // lại của chính nó.
  const identity = `${src}|${variant}`;
  const [seen, setSeen] = useState(identity);
  const [cacheKey, setCacheKey] = useState(freshCacheKey);
  // Chốt MỘT LẦN mỗi lần đổi ảnh, không tính lại mỗi lần vẽ: hết mốc "vừa tải"
  // giữa chừng mà đổi src là trình duyệt tải lại ảnh ngay trước mắt người dùng.
  const [freshOrig, setFreshOrig] = useState(() => freshOriginalUrl(src));
  if (seen !== identity) {
    setSeen(identity);
    setAttempt(0);
    setCacheKey(freshCacheKey());
    setFreshOrig(freshOriginalUrl(src));
  }

  // `failed` tách khỏi `attempt` vì <img> báo lỗi ngay, còn lần thử kế phải đợi
  // hết mốc trễ.
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!failed) return;
    const delay = RETRY_DELAYS_MS[attempt];
    if (delay === undefined) return; // hết nấc, để ảnh vỡ
    const t = setTimeout(() => {
      setAttempt((n) => n + 1);
      setFailed(false);
    }, delay);
    return () => clearTimeout(t);
  }, [failed, attempt]);

  const base = freshOrig ?? imageUrl(src, variant);
  const url =
    attempt === 0 ? base : base + (base.includes("?") ? "&" : "?") + `retry=${attempt}-${cacheKey}`;

  return <img src={url} alt={alt} {...rest} onError={() => setFailed(true)} />;
}
